/**
 * TextClip
 *
 * A user-defined text overlay clip rendered via Pixi Text.
 * - Does not depend on transcript/script content.
 * - Supports per-scene style/text overrides (keyed by script scene startSplitId, "__start__" for first scene).
 */

import { Clip } from "../clip";
import { VisualMixin } from "../mixins";
import { visualize } from "../mixins/visual.decorator";
import { serializable } from "../../services";
import { Graphics, Text, TextStyle } from "pixi.js";

import type { Track } from "../../tracks";
import type { ClipType } from "../clip";
import type { Timestamp } from "../../models";
import type { CaptionsTextAlign, CaptionsTextBaseline, CaptionsTextCase, CaptionsTextStyle } from "../captions/captions.types";
import type { TextClipProps } from "./text.interfaces";
import type { SceneTextStyleOverride } from "./text.types";
import { CompositionEventsEnum } from "../../../librettoEditor/CompositionEvents";
import { EngineInternalEventsEnum } from "../../EngineInternalEvents";

const DEFAULT_TEXT_STYLE: CaptionsTextStyle = {
    fontFamily: "Roboto",
    fontSize: 56,
    fontWeight: "bold",
    fontStyle: "normal",
    fillColor: "#FFFFFF",
    stroke: { color: "#000000", width: 4, join: "round" },
};

export class TextClip extends VisualMixin(Clip<TextClipProps>) {
    public readonly type: ClipType = "text";
    public track?: Track<TextClip>;

    @serializable()
    private _text: string = "Text";

    @serializable()
    private _textAlign: CaptionsTextAlign = "center";

    @serializable()
    private _textBaseline: CaptionsTextBaseline = "top";

    @serializable()
    private _textCase: CaptionsTextCase = "none";

    @serializable()
    private _maxWidth: number | string = 720;

    @serializable()
    private _textStyle: CaptionsTextStyle = { ...DEFAULT_TEXT_STYLE };

    /**
     * Per-scene style/text overrides.
     * Keyed by the ScriptLayerScene startSplitId (stable across insertions/deletions),
     * with a special key "__start__" for the first scene.
     *
     * Note: Persisted by editor via CompositionPersistenceService (Firestore-safe key mapping).
     */
    @serializable()
    private _sceneStyleOverrides: Record<string, SceneTextStyleOverride> = {};

    // Rendering objects
    private _box = new Graphics();
    private _textObj: Text;
    private _pixiTextStyle: TextStyle;

    // Track effective scene for fast updates
    private _currentSceneKey: string | null = null;
    private _effectiveText: string = this._text;
    private _effectiveTextAlign: CaptionsTextAlign = this._textAlign;
    private _effectiveTextStyle: CaptionsTextStyle = { ...this._textStyle };
    private _effectiveMaxWidth: number | string = this._maxWidth;
    private _dirty = true;

    public constructor(props?: TextClipProps) {
        super();

        this._pixiTextStyle = new TextStyle({
            fontFamily: this._effectiveTextStyle.fontFamily,
            fontSize: this._effectiveTextStyle.fontSize,
            fontWeight: (this._effectiveTextStyle.fontWeight as any) ?? "normal",
            fontStyle: (this._effectiveTextStyle as any).fontStyle ?? "normal",
            fill: this._effectiveTextStyle.fillColor,
            wordWrap: true,
            wordWrapWidth: 600,
        });

        this._textObj = new Text({ text: this.transformText(this._effectiveText), style: this._pixiTextStyle });

        // Invisible box for stable bounds / resize handles.
        // (alpha 0 still contributes to bounds for selection/drag-resize overlay)
        this.view.addChild(this._box);
        this.view.addChild(this._textObj);

        if (props) {
            this.applyProps(props);
        } else {
            this.recomputeEffectiveStyle();
            this.applyEffectiveStyleToPixi();
        }

        this.on("update", this.handleUpdate.bind(this));
    }

    // ────────────────────────────────────────────────────────────────────────────
    // Public properties
    // ────────────────────────────────────────────────────────────────────────────

    @serializable()
    public get text(): string {
        return this._text;
    }
    public set text(value: string) {
        this._text = String(value ?? "");
        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.forceRerender();
    }

    @serializable()
    public get textAlign(): CaptionsTextAlign {
        return this._textAlign;
    }
    public set textAlign(value: CaptionsTextAlign) {
        this._textAlign = value;
        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.forceRerender();
    }

    @serializable()
    public get textBaseline(): CaptionsTextBaseline {
        return this._textBaseline;
    }
    public set textBaseline(value: CaptionsTextBaseline) {
        this._textBaseline = value;
        this._dirty = true;
        this.forceRerender();
    }

    @serializable()
    public get textCase(): CaptionsTextCase {
        return this._textCase;
    }
    public set textCase(value: CaptionsTextCase) {
        this._textCase = value;
        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.forceRerender();
    }

    @serializable()
    public get maxWidth(): number | string {
        return this._maxWidth;
    }
    public set maxWidth(value: number | string) {
        this._maxWidth = value;
        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.forceRerender();
    }

    public get textStyle(): CaptionsTextStyle {
        return this._textStyle;
    }
    public set textStyle(value: Partial<CaptionsTextStyle>) {
        this._textStyle = { ...this._textStyle, ...(value ?? {}) };
        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.forceRerender();
    }

    public get sceneStyleOverrides(): Record<string, SceneTextStyleOverride> {
        return this._sceneStyleOverrides;
    }

    public updateTextStyleForScene(sceneKey: string | null, value: Partial<CaptionsTextStyle>): void {
        if (!sceneKey) {
            this.textStyle = value;
            try {
                const keys = Object.keys(value ?? {});
                if (keys.length > 0 && this._sceneStyleOverrides && typeof this._sceneStyleOverrides === "object") {
                    const next: Record<string, SceneTextStyleOverride> = { ...this._sceneStyleOverrides };
                    for (const [k, ov] of Object.entries(next)) {
                        if (!ov || typeof ov !== "object") continue;
                        if (!ov.textStyle || typeof ov.textStyle !== "object") continue;
                        const ts: any = { ...(ov.textStyle as any) };
                        for (const prop of keys) delete ts[prop];
                        const updated: any = { ...ov };
                        if (Object.keys(ts).length === 0) delete updated.textStyle;
                        else updated.textStyle = ts;
                        if (!updated.textStyle && !updated.textAlign && updated.text === undefined && updated.maxWidth === undefined) delete next[k];
                        else next[k] = updated;
                    }
                    this._sceneStyleOverrides = next;
                }
            } catch {}
            this.recomputeEffectiveStyle();
            this._dirty = true;
            this.forceRerender();
            return;
        }
        const prev = this._sceneStyleOverrides[sceneKey] ?? {};
        this._sceneStyleOverrides = {
            ...this._sceneStyleOverrides,
            [sceneKey]: {
                ...prev,
                textStyle: { ...(prev.textStyle ?? {}), ...value },
            },
        };
        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.forceRerender();
    }

    public updateTextAlignForScene(sceneKey: string | null, value: CaptionsTextAlign): void {
        if (!sceneKey) {
            this.textAlign = value;
            try {
                if (this._sceneStyleOverrides && typeof this._sceneStyleOverrides === "object") {
                    const next: Record<string, SceneTextStyleOverride> = { ...this._sceneStyleOverrides };
                    for (const [k, ov] of Object.entries(next)) {
                        if (!ov || typeof ov !== "object") continue;
                        if (ov.textAlign === undefined) continue;
                        const updated: any = { ...ov };
                        delete updated.textAlign;
                        if (!updated.textStyle && !updated.textAlign && updated.text === undefined && updated.maxWidth === undefined) delete next[k];
                        else next[k] = updated;
                    }
                    this._sceneStyleOverrides = next;
                }
            } catch {}
            this.recomputeEffectiveStyle();
            this._dirty = true;
            this.forceRerender();
            return;
        }
        const prev = this._sceneStyleOverrides[sceneKey] ?? {};
        this._sceneStyleOverrides = {
            ...this._sceneStyleOverrides,
            [sceneKey]: {
                ...prev,
                textAlign: value,
            },
        };
        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.forceRerender();
    }

    public updateTextForScene(sceneKey: string | null, value: string): void {
        const nextText = String(value ?? "");
        if (!sceneKey) {
            this.text = nextText;
            try {
                if (this._sceneStyleOverrides && typeof this._sceneStyleOverrides === "object") {
                    const next: Record<string, SceneTextStyleOverride> = { ...this._sceneStyleOverrides };
                    for (const [k, ov] of Object.entries(next)) {
                        if (!ov || typeof ov !== "object") continue;
                        if (ov.text === undefined) continue;
                        const updated: any = { ...ov };
                        delete updated.text;
                        if (!updated.textStyle && !updated.textAlign && updated.text === undefined && updated.maxWidth === undefined) delete next[k];
                        else next[k] = updated;
                    }
                    this._sceneStyleOverrides = next;
                }
            } catch {}
            this.recomputeEffectiveStyle();
            this._dirty = true;
            this.forceRerender();
            return;
        }
        const prev = this._sceneStyleOverrides[sceneKey] ?? {};
        this._sceneStyleOverrides = {
            ...this._sceneStyleOverrides,
            [sceneKey]: { ...prev, text: nextText },
        };
        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.forceRerender();
    }

    public updateMaxWidthForScene(sceneKey: string | null, value: number | string): void {
        if (!sceneKey) {
            this.maxWidth = value;
            try {
                if (this._sceneStyleOverrides && typeof this._sceneStyleOverrides === "object") {
                    const next: Record<string, SceneTextStyleOverride> = { ...this._sceneStyleOverrides };
                    for (const [k, ov] of Object.entries(next)) {
                        if (!ov || typeof ov !== "object") continue;
                        if (ov.maxWidth === undefined) continue;
                        const updated: any = { ...ov };
                        delete updated.maxWidth;
                        if (!updated.textStyle && !updated.textAlign && updated.text === undefined && updated.maxWidth === undefined) delete next[k];
                        else next[k] = updated;
                    }
                    this._sceneStyleOverrides = next;
                }
            } catch {}
            this.recomputeEffectiveStyle();
            this._dirty = true;
            this.forceRerender();
            return;
        }
        const prev = this._sceneStyleOverrides[sceneKey] ?? {};
        this._sceneStyleOverrides = {
            ...this._sceneStyleOverrides,
            [sceneKey]: { ...prev, maxWidth: value },
        };
        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.forceRerender();
    }

    /**
     * Used by the editor's HTML overlay while editing text, to avoid "double-rendering"
     * (Pixi text underneath the textarea).
     * Not persisted.
     */
    public setEditingMode(isEditing: boolean): void {
        try {
            this._textObj.visible = !isEditing;
        } catch {}
    }

    public set(props?: TextClipProps): this {
        if (props) {
            this.applyProps(props);
        }
        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.forceRerender();
        return this;
    }

    // ────────────────────────────────────────────────────────────────────────────
    // Engine update/render
    // ────────────────────────────────────────────────────────────────────────────

    @visualize
    public update(_time: Timestamp): void {
        // Per-scene style application happens here because Composition.currentSceneKey is
        // set by Composition.computeFrameAtTime().
        this.applySceneStyleIfNeeded();
    }

    // ────────────────────────────────────────────────────────────────────────────
    // Internals
    // ────────────────────────────────────────────────────────────────────────────

    private applyProps(props: TextClipProps): void {
        if (props.text !== undefined) this._text = String(props.text);
        if (props.textAlign !== undefined) this._textAlign = props.textAlign;
        if (props.textBaseline !== undefined) this._textBaseline = props.textBaseline;
        if (props.textCase !== undefined) this._textCase = props.textCase;
        if (props.maxWidth !== undefined) this._maxWidth = props.maxWidth;
        if (props.textStyle) this._textStyle = { ...this._textStyle, ...props.textStyle };

        // Visual mixin props
        if ((props as any).position !== undefined) (this as any).position = (props as any).position;
        if ((props as any).alpha !== undefined) (this as any).alpha = (props as any).alpha;
        if ((props as any).rotation !== undefined) (this as any).rotation = (props as any).rotation;

        // Clip props
        if (props.start !== undefined) this.start = props.start;
        if (props.stop !== undefined) this.stop = props.stop;
        if (props.name !== undefined) this._name = props.name;

        this.recomputeEffectiveStyle();
        this._dirty = true;
        this.applySceneStyleIfNeeded();
    }

    private handleUpdate(): void {
        this.applySceneStyleIfNeeded();
    }

    private getMaxWidthPixels(value: number | string): number {
        if (typeof value === "number") return Math.max(10, value);
        const compWidth = this.track?.composition?.width ?? 1920;
        const pct = Number.parseFloat(String(value ?? "0")) / 100;
        if (!Number.isFinite(pct) || pct <= 0) return 600;
        return Math.max(10, compWidth * pct);
    }

    private transformText(text: string): string {
        const t = String(text ?? "");
        if (this._textCase === "upper") return t.toUpperCase();
        if (this._textCase === "lower") return t.toLowerCase();
        return t;
    }

    private applySceneStyleIfNeeded(): void {
        const key = ((this.track?.composition as any)?.currentSceneKey as string | undefined) ?? null;
        if (key !== this._currentSceneKey) {
            this._currentSceneKey = key;
            this.recomputeEffectiveStyle();
            this._dirty = true;
        }
        if (!this._dirty) return;
        this.applyEffectiveStyleToPixi();
        this._dirty = false;
    }

    private recomputeEffectiveStyle(): void {
        const key = this._currentSceneKey;
        const override = key ? this._sceneStyleOverrides[key] : undefined;

        this._effectiveText = override?.text ?? this._text;
        this._effectiveTextAlign = (override?.textAlign ?? this._textAlign) as CaptionsTextAlign;
        this._effectiveMaxWidth = override?.maxWidth ?? this._maxWidth;
        this._effectiveTextStyle = {
            ...this._textStyle,
            ...(override?.textStyle ?? {}),
        };
    }

    private applyEffectiveStyleToPixi(): void {
        const style = this._effectiveTextStyle ?? this._textStyle;

        this._pixiTextStyle.fontFamily = style.fontFamily;
        this._pixiTextStyle.fontSize = style.fontSize;
        this._pixiTextStyle.fontWeight = (style.fontWeight as any) ?? "normal";
        this._pixiTextStyle.fontStyle = (style as any).fontStyle ?? "normal";
        this._pixiTextStyle.fill = style.fillColor;

        if (style.stroke) {
            this._pixiTextStyle.stroke = {
                color: style.stroke.color,
                width: style.stroke.width,
                join: style.stroke.join ?? "round",
                miterLimit: style.stroke.miterLimit,
            } as any;
        } else {
            (this._pixiTextStyle as any).stroke = undefined;
        }

        if (style.shadow) {
            this._pixiTextStyle.dropShadow = {
                color: style.shadow.color,
                blur: style.shadow.blur,
                distance: style.shadow.distance,
                angle: style.shadow.angle,
                alpha: style.shadow.alpha,
            } as any;
        } else {
            this._pixiTextStyle.dropShadow = false as any;
        }

        const wrapW = this.getMaxWidthPixels(this._effectiveMaxWidth);
        this._pixiTextStyle.wordWrap = true;
        this._pixiTextStyle.wordWrapWidth = wrapW;

        // NOTE: Pixi v8 caches texture per text+style; ensure it sees updates.
        this._textObj.style = this._pixiTextStyle;
        this._textObj.text = this.transformText(this._effectiveText);

        // Baseline: approximate by shifting y based on current bounds.
        this._textObj.y = 0;

        // Align inside the box (helps when text is shorter than wrap width).
        this._textObj.x = 0;
        try {
            const tw = Number((this._textObj as any).width ?? 0);
            if (this._effectiveTextAlign === "center") {
                this._textObj.x = Math.max(0, (wrapW - tw) / 2);
            } else if (this._effectiveTextAlign === "right") {
                this._textObj.x = Math.max(0, wrapW - tw);
            }
        } catch {}

        // Rebuild invisible bounds box (stable selection area for resizing)
        try {
            const h = Math.max(1, Number((this._textObj as any).height ?? 1));
            const left = -wrapW / 2;
            const top = -h / 2;

            // Center the box around (0,0) so clip.x/clip.y (view.position) are the visual center.
            // This matches DragResizeRotateContainer's drag math (which uses bounds top-left + width/2).
            this._textObj.y = top;
            // Align within the box, but offset to the centered origin.
            const tw = Math.max(0, Number((this._textObj as any).width ?? 0));
            let alignOffset = 0;
            if (this._effectiveTextAlign === "center") alignOffset = Math.max(0, (wrapW - tw) / 2);
            else if (this._effectiveTextAlign === "right") alignOffset = Math.max(0, wrapW - tw);
            this._textObj.x = left + alignOffset;

            this._box.clear();
            this._box.beginFill(0x000000, 0);
            this._box.drawRect(left, top, wrapW, h);
            this._box.endFill();
        } catch {}
    }

    private forceRerender(): void {
        this.trigger("update", undefined);
        try {
            const comp: any = (this.track as any)?.composition;
            if (comp) {
                comp.trigger(CompositionEventsEnum.CLIP_UPDATED as any, { clip: this } as any);
                comp.trigger(EngineInternalEventsEnum.CLIP_UPDATED as any, { clip: this } as any);
            }
        } catch {}
    }
}
